import React, { useState, useEffect } from "react";
import axios from "axios";
import DeleteButton from "../../../components/common/buttons/DeleteButton";
import LoadingIndicator from "../../../components/common/loadingIndicator/LoadingIndicator";
import InputField from "../../../components/common/inputs/InputField";
import {
  ChevronDown,
  ChevronUp,
  CopyMinus,
  CopyPlus,
  FilePenLine,
  X,
} from "lucide-react";

const Offers: React.FC = () => {
  const [topic, setTopic] = useState<string>("");
  const [subtopic, setSubtopic] = useState<string>("");
  const [paymentType, setPaymentType] = useState<string>("");
  const [useCode, setUseCode] = useState<string>("");
  const [expirationDate, setExpirationDate] = useState<string>("");

  const [image, setImage] = useState<File | null>(null);
  const [mobileImage, setMobileImage] = useState<File | null>(null);
  const [terms, setTerms] = useState<string[]>([]);
  const [newTerm, setNewTerm] = useState<string>("");

  const [offers, setOffers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [editingOffer, setEditingOffer] = useState<any>(null);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/content`);
      
      // Fix terms that were broken by TypeORM simple-array splitting by commas
      const fixedOffers = response.data.map((offer: any) => {
        let fixedTerms = offer.terms;
        if (Array.isArray(offer.terms)) {
          try {
            fixedTerms = JSON.parse(offer.terms.toString());
          } catch (e) {
            fixedTerms = offer.terms;
          }
        } else if (typeof offer.terms === 'string') {
          try {
            fixedTerms = JSON.parse(offer.terms);
          } catch (e) {
            fixedTerms = offer.terms;
          }
        }
        return { ...offer, terms: fixedTerms };
      });
      
      setOffers(fixedOffers);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching offers:", error);
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (file.size > 1024 * 1024) {
        alert("Image size should not exceed 1MB.");
        return;
      }

      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const { width, height } = img;
        if (width > 1600 || height > 1100) {
          alert("Image dimensions should not exceed 1600x1100.");
          return;
        }

        setImage(file);
      };
    }
  };

  const handleMobileFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (file.size > 1024 * 1024) {
        alert("Image size should not exceed 1MB.");
        return;
      }

      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        // Optional: Add dimension validation for mobile image if needed
        // const { width, height } = img;
        // if (width > 640 || height > 480) {
        //   alert("Mobile Image dimensions should not exceed 640x480.");
        //   return;
        // }

        setMobileImage(file);
      };
    }
  };

  const handleAddTerm = () => {
    if (newTerm.trim().length > 70) {
      alert("Each term should not exceed 70 characters.");
      return;
    }
    if (terms.length >= 10) {
      alert("You can only add up to 10 terms.");
      return;
    }
    if (newTerm.trim()) {
      setTerms([...terms, newTerm]);
      setNewTerm("");
    }
  };

  const handleRemoveTerm = (index: number) => {
    setTerms(terms.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("topic", topic);
    formData.append("subtopic", subtopic);
    formData.append("paymentType", paymentType);
    formData.append("useCode", useCode);
    formData.append("expirationDate", expirationDate);
    formData.append("terms", JSON.stringify(terms));
    if (image) {
      formData.append("image", image);
    }
    if (mobileImage) {
      formData.append("mobileImage", mobileImage);
    }

    try {
      if (editingOffer) {
        // Update existing offer
        const response = await axios.put(
          `${API_URL}/content/${editingOffer.id}?type=content`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        console.log("Offer updated:", response.data);
      } else {
        const response = await axios.post(
          `${API_URL}/content?type=content`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        console.log("Offer created:", response.data);
      }
      fetchOffers();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Error saving offer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (offer: any) => {
    setEditingOffer(offer);
    setTopic(offer.topic);
    setSubtopic(offer.subtopic);
    setPaymentType(offer.paymentType);
    setUseCode(offer.useCode);

    const formattedDate = new Date(offer.expirationDate)
      .toISOString()
      .split("T")[0];
    setExpirationDate(formattedDate);

    let parsedTerms = [];
    if (offer.terms) {
      if (Array.isArray(offer.terms)) {
        parsedTerms = offer.terms;
      } else if (typeof offer.terms === 'string') {
        try {
          parsedTerms = JSON.parse(offer.terms);
        } catch (e) {
          parsedTerms = [];
        }
      }
    }
    setTerms(parsedTerms);
    setImage(null);
    setMobileImage(null);

    setShowModal(true);
  };

  const resetForm = () => {
    setEditingOffer(null);
    setTopic("");
    setSubtopic("");
    setPaymentType("");
    setUseCode("");
    setExpirationDate("");
    setImage(null);
    setMobileImage(null);
    setTerms([]);
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/content/${id}?type=content`);
      console.log("Offer deleted");
      fetchOffers();
    } catch (error) {
      console.error("Error deleting offer:", error);
    }
  };

  const toggleExpand = (index: number) => {
    setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  return (
    <div className="p-4 border-t">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-xl font-semibold my-4">
            Offers List ({offers?.length || 0})
          </h3>
        </div>
        <div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-md"
          >
            Add Offer +
          </button>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 ">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative max-h-[95%] my-56 overflow-y-auto">
            <button
              className="absolute top-6 right-6 w-4 text-gray-500 hover:text-gray-700"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
            >
              <X />
            </button>
            <h3 className="text-lg font-semibold mb-4">
              {editingOffer ? "Edit Offer" : "Add New Offer"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <InputField
                type="text"
                placeholder="Topic"
                value={topic}
                onChange={setTopic}
                label="Topic"
              />
              <InputField
                type="text"
                placeholder="Subtopic"
                value={subtopic}
                onChange={setSubtopic}
                label="Subtopic"
              />
              <InputField
                type="text"
                placeholder="Payment Type"
                value={paymentType}
                onChange={setPaymentType}
                label="Payment Type"
              />
              <InputField
                type="text"
                placeholder="Use Code"
                value={useCode}
                onChange={setUseCode}
                label="Use Code"
              />
              <InputField
                type="date"
                value={expirationDate}
                onChange={setExpirationDate}
                label="Expiration Date"
              />
              <div>
                <label className="block text-sm text-gray-700">
                  Upload Desktop Image <span className="text-gray-400 text-xs">(1600x1100 Recommended)</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="border p-2 w-full rounded-md"
                />
                {editingOffer?.imageUrl && !image && (
                  <div className="mt-2">
                    <img
                      src={editingOffer.imageUrl}
                      alt="Existing Offer"
                      className="w-full h-48 object-cover rounded-md"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700">
                  Upload Mobile Image <span className="text-gray-400 text-xs">(640x480 Recommended)</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMobileFileChange}
                  className="border p-2 w-full rounded-md"
                />
                {editingOffer?.mobileImageUrl && !mobileImage && (
                  <div className="mt-2">
                    <img
                      src={editingOffer.mobileImageUrl}
                      alt="Existing Mobile Offer"
                      className="w-full h-48 object-cover rounded-md"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700">
                  Terms and Conditions
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTerm}
                    onChange={(e) => setNewTerm(e.target.value)}
                    className="border p-2 w-full rounded-md list-item"
                    placeholder="Add a term"
                  />
                  <button type="button" onClick={handleAddTerm}>
                    <CopyPlus />
                  </button>
                </div>
                <ol className="list-decimal pl-4">
                  {terms.map((term, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center mb-1"
                    >
                      <span>{term}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTerm(index)}
                      >
                        <CopyMinus size={18} />
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded-md w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <LoadingIndicator />
                ) : editingOffer ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingIndicator />
      ) : (
        <div>
          <div className="my-2">
            <div className="justify-start align-top ">
              {offers.length > 0 ? (
                offers.map((offer, index) => (
                  <React.Fragment key={index}>
                    <div
                      className="hover:bg-gray-50 border  my-2 cursor-pointer rounded-lg shadow-lg "
                      onClick={() => toggleExpand(index)}
                    >
                      <div className="p-2 flex justify-between items-center">
                        <div className="flex flex-col col-span-3">
                          <div className="font-bold pb-2">
                            {offer.topic.toUpperCase()}
                          </div>
                          <div className="text-sm">{offer.subtopic}</div>
                        </div>

                        <div className="flex gap-2">
                          <div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(offer);
                              }}
                              className="text-blue-500"
                            >
                              <FilePenLine
                                className="text-indigo-600 hover:text-green-600 mr-2"
                                size={18}
                              />
                            </button>
                          </div>
                          <div>
                            <DeleteButton
                              onConfirm={() => handleDelete(offer.id)}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="p-2 flex justify-between items-center border-t">
                        <span className="text-blue-500 text-sm">
                          {expandedIndex === index
                            ? "Hide Details"
                            : "Show Details"}
                        </span>
                        {expandedIndex === index ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                    </div>
                    {expandedIndex === index && (
                      <div className="bg-gray-100  flex flex-row ">
                        <div className="p-4 border-r">
                          {offer.terms && offer.terms.length > 0 ? (
                            <ul className="list-disc pl-4 text-justify">
                              {typeof offer.terms === "string"
                                ? JSON.parse(offer.terms).map(
                                  (term: string, termIndex: number) => (
                                    <li key={termIndex}>
                                      {term.replace(/[\\[\]"]/g, "")}
                                    </li>
                                  )
                                )
                                : offer.terms.map(
                                  (term: string, termIndex: number) => (
                                    <li key={termIndex}>
                                      {term.replace(/[\\[\]"]/g, "")}
                                    </li>
                                  )
                                )}
                            </ul>
                          ) : (
                            <span>No terms available</span>
                          )}
                        </div>
                        <div className="p-2 border-r">{offer.paymentType}</div>
                        <div className="p-2 border-r">{offer.useCode}</div>
                        <div className="p-2 border-r">
                          {new Date(offer.expirationDate).toLocaleDateString()}
                        </div>
                        <div className="p-2 w-1/2 h-1/2">
                          {offer.imageUrl ? (
                            <img
                              src={offer.imageUrl}
                              alt="Offer"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            "No Image"
                          )}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="p-2 text-center">
                    No offers available
                  </td>
                </tr>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Offers;
